import { IconPlus, IconTrash } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { Button, Form, Input, Modal } from "antd";
import { useEffect, useState } from "react";
import {
	useCreateWorkspace,
	useDeleteWorkspace,
	useWorkspaces,
} from "@/common/artemis-operations/workspaces";
import type { Workspace } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import {
	BaseContainer,
	BasePageContainer,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { WorkspaceTable } from "@/components/workspaces/workspace-table";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/admin/workspaces",
)({
	component: AdminWorkspacesPage,
});

function CreateWorkspaceModal({
	open,
	onClose,
}: {
	open: boolean;
	onClose: () => void;
}) {
	const [form] = Form.useForm<{ name: string }>();
	const notify = useNotify();
	const create = useCreateWorkspace();

	function handleClose() {
		form.resetFields();
		onClose();
	}

	function handleOk() {
		form.validateFields().then(({ name }) => {
			create.mutate(
				{ name },
				{
					onSuccess: () => {
						notify.success("Workspace created.");
						handleClose();
					},
					onError: (err) =>
						notify.error(
							(err as Error).message || "Failed to create workspace.",
						),
				},
			);
		});
	}

	return (
		<Modal
			title="Create workspace"
			open={open}
			onCancel={handleClose}
			onOk={handleOk}
			okText="Create"
			confirmLoading={create.isPending}
			destroyOnHidden
		>
			<Form form={form} layout="vertical" style={{ marginTop: 16 }}>
				<Form.Item
					name="name"
					label="Name"
					rules={[{ required: true, message: "Enter a workspace name" }]}
				>
					<Input placeholder="My workspace" autoFocus />
				</Form.Item>
			</Form>
		</Modal>
	);
}

function AdminWorkspacesPage() {
	const { setTitle } = usePageTitle();
	useEffect(() => setTitle("Workspaces"), [setTitle]);
	const notify = useNotify();
	const [addOpen, setAddOpen] = useState(false);
	const workspaces = useWorkspaces();
	const deleteWorkspace = useDeleteWorkspace();
	const { confirm, dialog } = useFrictionConfirm();

	const rows = (workspaces.data ?? []).filter(
		(ws): ws is NonNullable<Workspace> => ws != null,
	);

	const confirmDelete = (ws: NonNullable<Workspace>) => {
		confirm({
			title: "Delete workspace",
			message: `Delete "${ws.name}"? This cannot be undone.`,
			onConfirm: () => {
				deleteWorkspace(ws.id, {
					onSuccess: () => notify.success("Workspace deleted."),
					onError: (err) =>
						notify.error(
							(err as Error).message || "Failed to delete workspace.",
						),
				});
			},
		});
	};

	return (
		<BasePageContainer>
			<BaseContainer
				title=""
				actions={
					<Button
						icon={<CustomIcon icon={IconPlus} />}
						type="text"
						onClick={() => setAddOpen(true)}
					>
						Add
					</Button>
				}
			>
				<QueryGuard
					isPending={workspaces.isPending}
					isError={workspaces.isError}
					isNull={false}
				>
					<WorkspaceTable
						data={rows}
						pageSize={50}
						rowActions={[
							{
								label: "Delete",
								icon: <CustomIcon icon={IconTrash} />,
								danger: true,
								onClick: confirmDelete,
							},
						]}
					/>
				</QueryGuard>

				<CreateWorkspaceModal
					open={addOpen}
					onClose={() => setAddOpen(false)}
				/>
				{dialog}
			</BaseContainer>
		</BasePageContainer>
	);
}
